// Components/Pages/Resources.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaFilePdf, FaDownload } from "react-icons/fa";

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await fetch('https://api.rigokenya.org/api/resources');
      if (!response.ok) {
        throw new Error('Failed to fetch resources');
      }
      const data = await response.json();
      setResources(data);
      setFilteredResources(data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setError('Failed to load resources. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from resources
  const getCategories = () => {
    const categories = resources.map(r => r.category).filter(Boolean);
    return ["All", ...new Set(categories)];
  };

  const categories = getCategories();

  // Filter resources by category
  const filterByCategory = (category) => {
    setActiveCategory(category);
    if (category === "All") {
      setFilteredResources(resources);
    } else {
      const filtered = resources.filter(r => r.category === category);
      setFilteredResources(filtered);
    }
  };

  // Handle download
  const handleDownload = (resource) => {
    if (resource.file_url) {
      const link = document.createElement('a');
      link.href = `https://api.rigokenya.org${resource.file_url}`;
      link.target = '_blank';
      link.download = resource.title || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert('Download URL not available');
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primaryGold mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading resources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 max-w-md mx-auto">
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={fetchResources}
            className="mt-4 bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-primaryBlue mb-4">
            Resources
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Access our reports, strategic documents, case studies, and policy briefs to learn more about our work and impact.
          </p>
          <div className="w-24 h-1 bg-primaryGold mx-auto mt-4"></div>
        </div>

        {/* Category Filters */}
        {categories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => filterByCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === category 
                    ? "bg-primaryGold text-primaryBlue" 
                    : "bg-white text-gray-600 hover:bg-gray-100"
                } shadow-sm hover:shadow-md`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Resources Grid */}
        {filteredResources.length === 0 ? (
          <div className="text-center py-12">
            <FaFilePdf className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No resources available in this category.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {filteredResources.map((resource) => (
              <div 
                key={resource.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:transform hover:-translate-y-1"
              >
                <div className="p-6">
                  {/* Icon and Category */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-4xl">{resource.icon || '📄'}</span>
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {resource.category || 'General'}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-primaryBlue mb-2 line-clamp-2">
                    {resource.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {resource.description || 'No description available'}
                  </p>

                  {/* Meta Info */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <FaFilePdf className="text-red-500" />
                      {resource.file_type || 'PDF'}
                    </span>
                    <span>{resource.file_size || 'N/A'}</span>
                    <span>{resource.date || ''}</span>
                  </div>

                  {/* Download Button */}
                  <button 
                    onClick={() => handleDownload(resource)}
                    className="w-full bg-primaryBlue text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <FaDownload />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">Need more information or have questions about our resources?</p>
          <Link
            to="/contact"
            className="inline-block bg-primaryGold text-primaryBlue px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Resources;